import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// 3rd party libraries
import { useDispatch, useSelector } from "react-redux";
import moment from "moment-timezone";

// Components
import MDBox from "components/MDBox";
import FTextField from "components/Form/FTextField";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import MDTypography from "components/MDTypography";
import FontComponent from "components/Responsive/fonts";
import { ModalBreakPoint } from "components/Responsive/BreakPoints";

// Examples
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import DataTable from "examples/Tables/DataTable";

// Layouts
import ProjectOrdeReqItemTable from "layouts/projectOrders/data/ProjectOrdeReqItemTable";
import ProjectOrderReqTemporaryItemTable from "layouts/projectOrders/data/ProjectOrderReqTemporaryItemTable";

// Redux
import { openSnackbar } from "redux/Slice/Notification";
import { EquipmentTypeThunk } from "redux/Thunks/Equipment";

// Utils
import Constants, {
  Common,
  FormFields,
  defaultData,
  ButtonTitles,
  Icons,
  Colors,
} from "utils/Constants";
import { formatKeyName, numericFieldValidation } from "utils/methods/methods";

const initialTableData = {
  rows: [],
  columns: [],
};

const initialRequestedItemsObj = {
  equipmentType: "",
  engineerRequestedQuantity: "",
  engineerComment: "",
};

const initialTemporaryItemsObj = {
  productName: "",
  engineerRequestedQuantity: "",
  engineerComment: "",
};

function AddShoppingCartItems({
  addShoppingCartObjList,
  setAddShoppingCartObjList,
  headerItemsObj,
  setHeaderItemsObj,
  shoppingCartItemsErrors,
  setShoppingCartItemsErrors,
  fromDetailedViewPage,
}) {
  const dispatch = useDispatch();

  const configData = useSelector((state) => state.config) || {};
  const userId = configData?.config?.[0]?.id;
  const equipmentTypeList = useSelector((state) => state.product.equipmentTypeList) || [];

  const [projectOptionsList, setProjectOptionsList] = useState([]);
  const [equipmentOptionsList, setEquipmentOptionsList] = useState([]);

  const [requestedItemsObj, setRequestedItemsObj] = useState(initialRequestedItemsObj);
  const [updateStateAtAddItem, setUpdateStateAtAddItem] = useState(false);

  const [shoppingCartItemsDataTable, setShoppingCartItemsDataTable] = useState(initialTableData);
  const [temporaryItemsDataTable, setTemporaryItemsDataTable] = useState(initialTableData);
  const [temporaryItemsObj, setTemporaryItemsObj] = useState(initialTemporaryItemsObj);
  const [temporaryItemsErrors, setTemporaryItemsErrors] = useState({});

  const deleteShoppingCartItemFunc = (equipmentType) => {
    setAddShoppingCartObjList((prev) => {
      const currentEquipmentOrders = Array.isArray(prev.equipmentOrders)
        ? prev.equipmentOrders
        : [];
      return {
        ...prev,
        equipmentOrders: currentEquipmentOrders.filter(
          (item) => item?.equipmentType !== equipmentType
        ),
      };
    });
    setUpdateStateAtAddItem((prev) => !prev);
  };

  const deleteTemporaryItemFunc = (id) => {
    setAddShoppingCartObjList((prev) => {
      const currentTempOrders = Array.isArray(prev.tempOrders) ? prev.tempOrders : [];
      return {
        ...prev,
        tempOrders: currentTempOrders.filter((item, index) => index !== id),
      };
    });
    setUpdateStateAtAddItem((prev) => !prev);
  };

  // New Shopping Cart table columns and rows
  const { requestedItemsColumns, requestedItemsRows } = ProjectOrdeReqItemTable({
    dataList: Array.isArray(addShoppingCartObjList?.equipmentOrders)
      ? addShoppingCartObjList.equipmentOrders
      : [],
    deleteReqItemFunc: deleteShoppingCartItemFunc,
    updateStateAtAddItem,
  });

  // Temporary Items table columns and rows
  const { temporaryItemsColumns, temporaryItemsRows } = ProjectOrderReqTemporaryItemTable({
    dataList: Array.isArray(addShoppingCartObjList?.tempOrders)
      ? addShoppingCartObjList.tempOrders
      : [],
    deleteTemporaryItemsFunc: deleteTemporaryItemFunc,
    updateStateAtAddItem,
  });

  // Equipment Type List
  const getEquipmentTypeListFunc = async () => {
    try {
      await dispatch(EquipmentTypeThunk());
    } catch (error) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const createCommentObjFunc = (dataObj = {}) => {
    if (!dataObj?.engineerComment || dataObj?.engineerComment?.trim() === "") {
      return null;
    }
    const commentObj = {
      user: userId,
      time: moment().format(Common.CURRENT_TIME_ZONE_FORMAT),
      status: Constants.QUEUE,
      comment: dataObj.engineerComment,
    };

    return commentObj;
  };

  const handleFormValueChange = (e, type = "") => {
    const { name, value } = e.target;

    if (type === Common.TEMPORARY_ITEMS_TYPES_TEXT) {
      setTemporaryItemsObj((prev) => ({ ...prev, [name]: value }));
      setTemporaryItemsErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    } else if (type === "header") {
      setHeaderItemsObj((prev) => ({ ...prev, [name]: value }));
      setShoppingCartItemsErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    } else {
      setRequestedItemsObj((prev) => ({ ...prev, [name]: value }));
      setShoppingCartItemsErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    let adjustedDate = null;

    // Only format the date if value is not empty/null
    if (value && value.trim() !== "") {
      adjustedDate = moment(value).format(defaultData.DATABSE_DATE_FORMAT);
    }

    setHeaderItemsObj((prev) => ({ ...prev, [name]: adjustedDate }));
    setShoppingCartItemsErrors((prev) => {
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const validateFormDataFunc = (formDataObj = {}, type = "") => {
    // Making comment optional by removing it from formData.
    const { engineerComment, ...formData } = formDataObj;
    const errors = {};

    if (type === Common.TEMPORARY_ITEMS_TYPES_TEXT) {
      // For temporary items, validate productName and quantity
      if (!formData.productName || formData.productName.trim() === "") {
        errors.productName = "Equipment name is required";
      }
      if (!formData.engineerRequestedQuantity || formData.engineerRequestedQuantity <= 0) {
        errors.engineerRequestedQuantity = "Quantity is required";
      }
      setTemporaryItemsErrors((prev) => ({ ...prev, ...errors }));
    } else {
      // For regular equipment items
      Object.keys(formData).forEach((key) => {
        const value = formData[key];
        // If the value is a string, trim and check if it's empty
        if (typeof value === "string" && value.trim() === "") {
          errors[key] = `${formatKeyName(key)} is required`;
        }
        // If the value is null or undefined (but allow numbers including 0)
        else if (value === null || value === undefined) {
          errors[key] = `${formatKeyName(key)} is required`;
        }
      });
      setShoppingCartItemsErrors((prev) => ({ ...prev, ...errors }));
    }

    return Object.keys(errors).length === 0;
  };

  // Get Equipment Details by Id and update the requested items obj
  const getEquipmentDetailsById = (dataObj) => {
    const { equipmentType } = dataObj;
    const equipmentDetails = equipmentTypeList.find(
      (item) => item[Constants.MONGOOSE_ID] === equipmentType
    );

    if (equipmentDetails) {
      const productCategory = equipmentDetails?.equipmentCategory?.name;
      const productType = equipmentDetails?.type;
      const quantityType = equipmentDetails?.quantityType?.priceType;

      const engineerRequestedQuantity = Number(dataObj?.engineerRequestedQuantity) || 0;
      const totalAmount = Number(equipmentDetails?.price || 0) * engineerRequestedQuantity || 0;

      return {
        ...dataObj,
        productCategory,
        productType,
        quantityType,
        totalAmount,
        engineerRequestedQuantity,
      };
    }

    return dataObj;
  };

  // Update existing equipment type internally without API call
  const updateExistingEquipmentTypeFunc = (existingIndex, newRequestedObj) => {
    if (!Array.isArray(addShoppingCartObjList?.equipmentOrders)) return;
    const existingItem = addShoppingCartObjList?.equipmentOrders[existingIndex];
    if (!existingItem?.totalAmount || !existingItem?.engineerRequestedQuantity) return;
    const unitPrice = existingItem.totalAmount / existingItem.engineerRequestedQuantity;

    const currQuantity = Number(newRequestedObj.engineerRequestedQuantity) || 0;
    const totalQuantity = currQuantity + existingItem.engineerRequestedQuantity;
    const totalAmount = unitPrice * totalQuantity;

    const commentObj = createCommentObjFunc(newRequestedObj);
    const newCommentArr = commentObj
      ? [...existingItem.engineerComment, commentObj]
      : existingItem.engineerComment;

    const updatedItemObj = {
      ...existingItem,
      engineerRequestedQuantity: totalQuantity,
      totalAmount,
      engineerComment: newCommentArr,
    };

    setAddShoppingCartObjList((prev) => {
      const currentEquipmentOrders = Array.isArray(prev.equipmentOrders)
        ? prev.equipmentOrders
        : [];
      return {
        ...prev,
        equipmentOrders: currentEquipmentOrders.map((item, index) =>
          index === existingIndex ? updatedItemObj : item
        ),
      };
    });
    setRequestedItemsObj(initialRequestedItemsObj);
  };

  // Add Equipment in Shopping Cart without API call
  const addEquipmentInShoppingCartFunc = (type = "") => {
    const isTemporary = type === Common.TEMPORARY_ITEMS_TYPES_TEXT;
    const itemsObj = isTemporary ? temporaryItemsObj : requestedItemsObj;
    const validateData = validateFormDataFunc({ ...headerItemsObj, ...itemsObj }, type);

    if (!validateData) {
      dispatch(
        openSnackbar({
          message: Constants.FILL_ALL_REQUIRED_FIELDS,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
      return;
    }

    const newUpdatedItemsObj = { ...itemsObj, project: headerItemsObj?.project };

    if (isTemporary) {
      // Handle temporary items
      const updatedQuantity = Number(newUpdatedItemsObj.engineerRequestedQuantity) || 0;
      const isItemExist = addShoppingCartObjList?.tempOrders?.findIndex(
        (item) =>
          item?.productName &&
          item.productName?.toLowerCase() === newUpdatedItemsObj.productName?.toLowerCase()
      );

      if (isItemExist !== -1 && Array.isArray(addShoppingCartObjList?.tempOrders)) {
        const existingItem = addShoppingCartObjList?.tempOrders[isItemExist];
        if (!existingItem) return;
        const existingQuantity = Number(existingItem.engineerRequestedQuantity) || 0;
        const totalQuantity = updatedQuantity + existingQuantity;

        const commentObj = createCommentObjFunc(newUpdatedItemsObj);
        const newCommentArr = commentObj
          ? [...existingItem.engineerComment, commentObj]
          : existingItem.engineerComment;

        const updatedItemObj = {
          ...existingItem,
          engineerRequestedQuantity: totalQuantity,
          engineerComment: newCommentArr,
        };

        setAddShoppingCartObjList((prev) => ({
          ...prev,
          tempOrders: prev.tempOrders.map((item, index) =>
            index === isItemExist ? updatedItemObj : item
          ),
        }));
      } else {
        const commentObj = createCommentObjFunc(newUpdatedItemsObj);
        const newCommentArr = commentObj ? [commentObj] : [];

        const newTempItemObj = {
          ...newUpdatedItemsObj,
          engineerRequestedQuantity: updatedQuantity,
          engineerComment: newCommentArr,
        };

        setAddShoppingCartObjList((prev) => {
          const currentTempOrders = Array.isArray(prev.tempOrders) ? prev.tempOrders : [];
          return {
            ...prev,
            tempOrders: [...currentTempOrders, newTempItemObj],
          };
        });
      }
      setTemporaryItemsObj(initialTemporaryItemsObj);
    } else {
      // Handle regular equipment items
      const isEquipmentTypeExist = Array.isArray(addShoppingCartObjList?.equipmentOrders)
        ? addShoppingCartObjList.equipmentOrders.findIndex(
            (item) => item?.equipmentType === newUpdatedItemsObj?.equipmentType
          )
        : -1;

      if (isEquipmentTypeExist !== -1) {
        updateExistingEquipmentTypeFunc(isEquipmentTypeExist, newUpdatedItemsObj);
        setUpdateStateAtAddItem((prev) => !prev);
        return;
      }

      const equipmentDetailsObj = getEquipmentDetailsById(newUpdatedItemsObj);
      const commentObj = createCommentObjFunc(newUpdatedItemsObj);
      const newCommentArr = commentObj ? [commentObj] : [];

      const newEquipmentDetailsObj = {
        ...equipmentDetailsObj,
        engineerComment: newCommentArr,
      };

      setAddShoppingCartObjList((prev) => {
        const currentEquipmentOrders = Array.isArray(prev.equipmentOrders)
          ? prev.equipmentOrders
          : [];
        return {
          ...prev,
          equipmentOrders: [...currentEquipmentOrders, newEquipmentDetailsObj],
        };
      });
      setRequestedItemsObj(initialRequestedItemsObj);
    }
    setUpdateStateAtAddItem((prev) => !prev);
  };

  useEffect(() => {
    const getProjectList = configData?.screens?.[5]?.screensInfo?.properties || [];
    const projectItem = getProjectList.find((item) => item.id === Common.PROJECT_TEXT);

    const createDropdownListFunc = (listArr = []) => {
      const temp = listArr.map((item) => ({
        title: item.title,
        [Constants.MONGOOSE_ID]: item.id,
      }));

      return temp;
    };

    if (projectItem) {
      const temp = createDropdownListFunc(projectItem.options);
      setProjectOptionsList(temp);
    }

    getEquipmentTypeListFunc();
  }, [configData]);

  useEffect(() => {
    if (equipmentTypeList?.length > 0) {
      const equipmentOptions = equipmentTypeList
        ?.filter((elem) => elem?.isTemporary === false)
        .map((item) => ({
          title: item.type,
          [Constants.MONGOOSE_ID]: item?.[Constants.MONGOOSE_ID],
        }))
        ?.sort((a, b) => a.title.localeCompare(b.title));

      setEquipmentOptionsList(equipmentOptions);
    }
  }, [equipmentTypeList]);

  useEffect(() => {
    setShoppingCartItemsDataTable({ rows: requestedItemsRows, columns: requestedItemsColumns });
  }, [addShoppingCartObjList?.equipmentOrders, updateStateAtAddItem]);

  useEffect(() => {
    setTemporaryItemsDataTable({ rows: temporaryItemsRows, columns: temporaryItemsColumns });
  }, [addShoppingCartObjList?.tempOrders, updateStateAtAddItem]);

  return (
    <MDBox display="flex" flexDirection="column" gap={2}>
      {/* Title header Form Fields container */}
      {!fromDetailedViewPage && (
        <MDBox display="flex" gap={2} justifyContent="flex-start">
          <MDBox flex="1 1 20%">
            <FTextField
              label={Common.PROJECT_ORDER_SHOPPING_CART_TITLE_LABEL}
              name={Common.PROJECT_ORDER_SHOPPING_CART_TITLE_NAME}
              id={Common.PROJECT_ORDER_SHOPPING_CART_TITLE_NAME}
              type={FormFields.TYPE_TEXT}
              placeholder={Common.PROJECT_ORDER_SHOPPING_CART_TITLE_LABEL}
              disabled={addShoppingCartObjList.length > 0}
              value={headerItemsObj[Common.PROJECT_ORDER_SHOPPING_CART_TITLE_NAME] || ""}
              error={Boolean(
                shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_TITLE_NAME]
              )}
              helperText={shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_TITLE_NAME]}
              handleChange={(e) => handleFormValueChange(e, "header")}
            />
          </MDBox>

          <MDBox flex="1 1 20%">
            <CustomAutoComplete
              label={Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_LABEL}
              name={Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_NAME}
              id={Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_NAME}
              hint={Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_LABEL}
              getOptionLabel={(option) => option.title || ""}
              menu={projectOptionsList}
              disabled={addShoppingCartObjList.length > 0}
              value={{
                title: projectOptionsList.find(
                  (item) =>
                    item[Constants.MONGOOSE_ID] ===
                    headerItemsObj[Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_NAME]
                )?.title,
              }}
              error={shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_NAME]}
              helperText={shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_PROJECT_NAME]}
              handleChange={(e) => handleFormValueChange(e, "header")}
            />
          </MDBox>

          <MDBox flex="0 1 20%">
            <FTextField
              label={Common.PROJECT_ORDER_SHOPPING_CART_FROM_DATE_LABEL}
              placeholder={defaultData.REACTDATETIMEPICKER_DATE_FORMAT}
              name={Common.PROJECT_ORDER_SHOPPING_CART_FROM_DATE_NAME}
              id={Common.PROJECT_ORDER_SHOPPING_CART_FROM_DATE_NAME}
              type="date"
              value={
                headerItemsObj?.fromDate
                  ? moment(headerItemsObj?.fromDate).format(defaultData.DATABSE_DATE_FORMAT)
                  : ""
              }
              error={Boolean(
                shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_FROM_DATE_NAME]
              )}
              helperText={
                shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_FROM_DATE_NAME]
              }
              handleChange={handleDateChange}
              InputProps={{
                inputProps: {
                  max: headerItemsObj?.toDate
                    ? moment(headerItemsObj?.toDate).format(defaultData.DATABSE_DATE_FORMAT)
                    : undefined,
                  style: { textTransform: "uppercase" },
                },
              }}
              onKeyPress={(e) => {
                // Prevent manual typing, only allow date picker selection
                e.preventDefault();
              }}
              disabled={addShoppingCartObjList.length > 0}
            />
          </MDBox>

          <MDBox flex="0 1 20%">
            <FTextField
              label={Common.PROJECT_ORDER_SHOPPING_CART_TO_DATE_LABEL}
              placeholder={defaultData.REACTDATETIMEPICKER_DATE_FORMAT}
              name={Common.PROJECT_ORDER_SHOPPING_CART_TO_DATE_NAME}
              id={Common.PROJECT_ORDER_SHOPPING_CART_TO_DATE_NAME}
              type="date"
              value={
                headerItemsObj?.toDate
                  ? moment(headerItemsObj?.toDate).format(defaultData.DATABSE_DATE_FORMAT)
                  : ""
              }
              error={Boolean(
                shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_TO_DATE_NAME]
              )}
              helperText={shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_TO_DATE_NAME]}
              handleChange={handleDateChange}
              InputProps={{
                inputProps: {
                  min: headerItemsObj?.fromDate
                    ? moment(headerItemsObj?.fromDate).format(defaultData.DATABSE_DATE_FORMAT)
                    : undefined,
                  style: { textTransform: "uppercase" },
                },
              }}
              onKeyPress={(e) => {
                // Prevent manual typing, only allow date picker selection
                e.preventDefault();
              }}
              disabled={addShoppingCartObjList.length > 0}
            />
          </MDBox>
        </MDBox>
      )}

      <MDBox display="flex" flexDirection="column" gap={2}>
        <MDBox>
          <MDTypography
            sx={{
              fontSize: FontComponent({ sizes: ModalBreakPoint.baseTitleBreakPoint }),
              color: "#475467",
              fontWeight: "700",
            }}
          >
            Equipment Items
          </MDTypography>
        </MDBox>

        {/* Box for Requested Items */}
        <MDBox display="flex" flexDirection="column" gap={1}>
          {/* Form Fields and Button Box */}
          <MDBox
            display="flex"
            flexDirection="row"
            gap={2}
            justifyContent="space-between"
            alignItems="bottom"
          >
            <MDBox
              display="flex"
              flexGrow={1}
              flexDirection="row"
              justifyContent="space-between"
              gap={2}
              key={updateStateAtAddItem}
              minHeight="85px"
            >
              <MDBox width="20%" minWidth="160px">
                <CustomAutoComplete
                  label={Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_LABEL}
                  name={Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_NAME}
                  id={Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_NAME}
                  hint={Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_LABEL}
                  getOptionLabel={(option) => option.title || ""}
                  menu={equipmentOptionsList}
                  value={{
                    title: equipmentOptionsList.find(
                      (item) =>
                        item[Constants.MONGOOSE_ID] ===
                        requestedItemsObj[Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_NAME]
                    )?.title,
                  }}
                  error={
                    shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_NAME]
                  }
                  helperText={
                    shoppingCartItemsErrors[Common.PROJECT_ORDER_SHOPPING_CART_EQUIPMENT_TYPE_NAME]
                  }
                  handleChange={(e) => handleFormValueChange(e)}
                />
              </MDBox>

              <MDBox width="20%" minWidth="160px">
                <FTextField
                  label={Common.PROJECT_ORDER_SHOPPING_CART_QUANTITY_LABEL}
                  name={Common.PROJECT_ORDER_SHOPPING_CART_QUANTITY_NAME}
                  id={Common.PROJECT_ORDER_SHOPPING_CART_QUANTITY_NAME}
                  type={FormFields.TYPE_NUMBER}
                  placeholder={Common.PROJECT_ORDER_QUANTITY_LABEL}
                  error={Boolean(shoppingCartItemsErrors.engineerRequestedQuantity)}
                  helperText={shoppingCartItemsErrors.engineerRequestedQuantity}
                  value={requestedItemsObj.engineerRequestedQuantity || ""}
                  onKeyPress={(e) => numericFieldValidation(e)}
                  handleChange={(e) => handleFormValueChange(e)}
                />
              </MDBox>

              <MDBox flexGrow={1} width="auto">
                <FTextField
                  label={Common.PROJECT_ORDDER_SHOPPING_CART_COMMENTS_LABEL}
                  name={Common.PROJECT_ORDER_SHOPPING_CART_COMMENTS_NAME}
                  id={Common.PROJECT_ORDER_SHOPPING_CART_COMMENTS_NAME}
                  type={FormFields.TYPE_TEXT}
                  placeholder={Common.PROJECT_ORDDER_SHOPPING_CART_COMMENTS_LABEL}
                  value={requestedItemsObj.engineerComment || ""}
                  handleChange={(e) => handleFormValueChange(e)}
                />
              </MDBox>
            </MDBox>

            <MDBox
              display="flex"
              justifyContent="flex-end"
              alignItems="flex-start"
              flexShrink={0}
              height="100%"
              pt={4.5}
            >
              <BasicButton
                title={ButtonTitles.ADD_ITEM}
                icon={Icons.ADD}
                background={Colors.WHITE}
                border
                color={Colors.BLACK}
                action={addEquipmentInShoppingCartFunc}
              />
            </MDBox>
          </MDBox>

          {/* Table Container */}
          <MDBox>
            {addShoppingCartObjList?.equipmentOrders?.length > 0 && (
              <DataTable
                table={shoppingCartItemsDataTable}
                isSorted={false}
                entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
                showTotalEntries={false}
                pagination={{ variant: "gradient", color: "info" }}
                loading={Constants.FULFILLED}
              />
            )}
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Box for Temporary Items */}
      <MDBox display="flex" flexDirection="column" gap={1}>
        {/* Temporary Items Title Box */}
        <MDBox mt={1} mb={0.5} ml={-1}>
          <MDTypography
            sx={{
              fontSize: FontComponent({ sizes: ModalBreakPoint.baseTitleBreakPoint }),
              color: "#475467",
              fontWeight: "700",
            }}
          >
            Temporary Items
          </MDTypography>
        </MDBox>

        {/* Temporary Form Fields and Button Box */}
        <MDBox
          display="flex"
          flexDirection="row"
          gap={2}
          justifyContent="space-between"
          alignItems="bottom"
        >
          <MDBox
            display="flex"
            flexGrow={1}
            flexDirection="row"
            gap={2}
            key={updateStateAtAddItem}
            minHeight="85px"
          >
            <MDBox width="20%" minWidth="160px">
              <FTextField
                label={Common.PROJECT_ORDER_TEMPORARY_PRODUCT_LABEL}
                name={Common.PROJECT_ORDER_TEMPORARY_PRODUCT_NAME}
                id={Common.PROJECT_ORDER_TEMPORARY_PRODUCT_NAME}
                type={FormFields.TYPE_TEXT}
                placeholder={Common.PROJECT_ORDER_TEMPORARY_PRODUCT_LABEL}
                error={Boolean(temporaryItemsErrors.productName)}
                helperText={temporaryItemsErrors.productName}
                value={temporaryItemsObj.productName || ""}
                handleChange={(e) => handleFormValueChange(e, Common.TEMPORARY_ITEMS_TYPES_TEXT)}
              />
            </MDBox>

            <MDBox width="20%" minWidth="160px">
              <FTextField
                label={Common.PROJECT_ORDER_TEMPORARY_QUANTITY_LABEL}
                name={Common.PROJECT_ORDER_TEMPORARY_QUANTITY_NAME}
                id={Common.PROJECT_ORDER_TEMPORARY_QUANTITY_NAME}
                type={FormFields.TYPE_NUMBER}
                placeholder={Common.PROJECT_ORDER_TEMPORARY_QUANTITY_LABEL}
                error={Boolean(temporaryItemsErrors.engineerRequestedQuantity)}
                helperText={temporaryItemsErrors.engineerRequestedQuantity}
                value={temporaryItemsObj.engineerRequestedQuantity}
                onKeyPress={(e) => numericFieldValidation(e)}
                handleChange={(e) => handleFormValueChange(e, Common.TEMPORARY_ITEMS_TYPES_TEXT)}
              />
            </MDBox>

            <MDBox flexGrow={1} width="auto">
              <FTextField
                label={Common.PROJECT_ORDER_COMMENTS_LABEL}
                name={Common.PROJECT_ORDER_COMMENTS_NAME}
                id={Common.PROJECT_ORDER_COMMENTS_NAME}
                type={FormFields.TYPE_TEXT}
                placeholder={Common.PROJECT_ORDER_COMMENTS_LABEL}
                value={temporaryItemsObj.engineerComment || ""}
                handleChange={(e) => handleFormValueChange(e, Common.TEMPORARY_ITEMS_TYPES_TEXT)}
              />
            </MDBox>
          </MDBox>

          <MDBox
            display="flex"
            justifyContent="flex-end"
            alignItems="flex-start"
            flexShrink={0}
            height="100%"
            pt={4.5}
          >
            <BasicButton
              title={ButtonTitles.ADD_ITEM}
              icon={Icons.ADD}
              background={Colors.WHITE}
              border
              color={Colors.BLACK}
              action={() => addEquipmentInShoppingCartFunc(Common.TEMPORARY_ITEMS_TYPES_TEXT)}
            />
          </MDBox>
        </MDBox>

        {/* Temporary Items Table */}
        <MDBox mt={0.5}>
          {addShoppingCartObjList?.tempOrders?.length > 0 && (
            <DataTable
              table={temporaryItemsDataTable}
              isSorted={false}
              entriesPerPage={{ defaultValue: defaultData.PER_PAGE_4 }}
              showTotalEntries={false}
              pagination={{ variant: "gradient", color: "info" }}
              loading={Constants.FULFILLED}
            />
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

AddShoppingCartItems.defaultProps = {
  fromDetailedViewPage: false,
};

AddShoppingCartItems.propTypes = {
  addShoppingCartObjList: PropTypes.arrayOf(PropTypes.object).isRequired,
  setAddShoppingCartObjList: PropTypes.func.isRequired,
  fromDetailedViewPage: PropTypes.bool,
  headerItemsObj: PropTypes.objectOf(PropTypes.any).isRequired,
  setHeaderItemsObj: PropTypes.func.isRequired,
  shoppingCartItemsErrors: PropTypes.objectOf(PropTypes.any).isRequired,
  setShoppingCartItemsErrors: PropTypes.func.isRequired,
};

export default AddShoppingCartItems;
